'use server'

import { apiRequest, ApiResponse } from '../request'

export type DashboardPeriod = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom'

export interface DashboardQueryParams {
    period?: DashboardPeriod
    startDate?: string
    endDate?: string
}

export interface ChartDataPoint {
    date: string
    value: number
    label?: string
}

export interface PieChartData {
    name: string
    value: number
    color?: string
}

export interface BarChartData {
    name: string
    value: number
    secondaryValue?: number
}

export interface TopProfessional {
    id: number
    name: string
    consultations: number
    specialty?: string
}

export interface HealthStats {
    totalConsultations: number
    consultationsToday: number
    consultationsWeek: number
    consultationsMonth: number
    consultationsPeriod: number
    consultationsGrowth: number
    consultationsByStatus: Record<string, number>
    averageConsultationDuration: number
    transcriptionCompletionRate: number

    totalExams: number
    examsPeriod: number
    examsGrowth: number
    pendingExams: number
    completedExams: number
    examsByStatus: Record<string, number>

    totalPrescriptions: number
    prescriptionsToday: number
    prescriptionsWeek: number
    prescriptionsPeriod: number
    prescriptionsGrowth: number

    totalOrders: number
    ordersPeriod: number
    ordersGrowth: number
    ordersByType: Record<string, number>
    ordersByStatus: Record<string, number>

    totalTriages: number
    triagesToday: number
    triagesPeriod: number
    triagesGrowth: number
    triagesByUrgency: Record<string, number>

    totalMedicalReports: number
    reportsByType: Record<string, number>

    topProfessionals: TopProfessional[]
}

export interface DashboardStats {
    totalAttendances: number
    attendancesToday: number
    attendancesWeek: number
    attendancesMonth: number
    attendancesPeriod: number
    attendancesGrowth: number

    totalClients: number
    newClientsToday: number
    newClientsWeek: number
    newClientsMonth: number
    newClientsPeriod: number
    clientsGrowth: number

    totalEmployees: number
    totalProfessionals: number
    totalDepartments: number
    totalAttendants: number
    totalProducts: number


    totalRevenue: number
    revenueToday: number
    revenueWeek: number
    revenueMonth: number
    revenuePeriod: number
    revenueGrowth: number
    averageTicket: number

    humanResponseAvgSeconds: number
    aiResponseAvgSeconds: number
    humanResponseCount: number
    aiResponseCount: number

    health?: HealthStats
}

export interface HealthCharts {
    consultationsByPeriod: ChartDataPoint[]
    consultationsByProfessional: BarChartData[]
    triagesByUrgency: PieChartData[]
    ordersByType: PieChartData[]
    examsByStatus: PieChartData[]
    topSpecialties: BarChartData[]
}

export interface DashboardCharts {
    attendancesByPeriod: ChartDataPoint[]
    revenueByPeriod: ChartDataPoint[]
    clientsByPeriod: ChartDataPoint[]
    paymentsByType: PieChartData[]

    health?: HealthCharts
}

export interface UpcomingEvent {
    id: number
    title: string
    type: 'consultation' | 'order'
    scheduledAt: string
    clientName: string
    professionalName?: string
    status: string
}

export interface PendingAction {
    type: 'attendance' | 'exam' | 'triage'
    count: number
    message: string
    href: string
    priority: 'high' | 'medium' | 'low'
}

export interface TeleconsultationMinutesInfo {
    current: number
    max: number | 'unlimited'
    percentage: number
    isNearLimit: boolean
    isAtLimit: boolean
}

export interface SubscriptionInfo {
    planName: string
    status: string
    /** Fim do período grátis concedido no cadastro (ausente = sem cortesia). */
    trialEndsAt?: string
    /** Dias inteiros restantes do período grátis (0 = sem cortesia em curso). */
    trialDaysRemaining?: number
    dueDate?: string
    teleconsultationMinutes?: TeleconsultationMinutesInfo
    fiscalNotes?: TeleconsultationMinutesInfo
}

export interface RecentActivity {
    type: string
    message: string
    timestamp: string
    icon: string
}

export interface DashboardWidgets {
    upcomingEvents: UpcomingEvent[]
    pendingActions: PendingAction[]
    subscription: SubscriptionInfo
    recentActivity: RecentActivity[]
}


export interface RecentClient {
    id: number
    name: string
    email?: string
    phone?: string
    createdAt: string
}

export interface RecentConsultation {
    id: number
    clientName: string
    professionalName: string
    status: string
    scheduledAt: string
    hasTranscription: boolean
}

export interface RecentTriage {
    id: number
    clientName: string
    urgency: string
    urgencyColor: string
    chiefComplaint: string
    triageAt: string
}

export interface DashboardTables {
    recentClients: RecentClient[]
    recentConsultations?: RecentConsultation[]
    recentTriages?: RecentTriage[]
}

export interface FullDashboardResponse {
    stats: DashboardStats
    charts: DashboardCharts
    widgets: DashboardWidgets
    tables: DashboardTables
    segment: string
    secondarySegment: string
    lastUpdated: string
}

// === API FUNCTIONS ===

export async function getFullDashboard(
    params?: DashboardQueryParams
): Promise<ApiResponse<FullDashboardResponse>> {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.startDate) searchParams.set('startDate', params.startDate)
    if (params?.endDate) searchParams.set('endDate', params.endDate)

    const query = searchParams.toString()
    return apiRequest<FullDashboardResponse>(
        `dashboard${query ? `?${query}` : ''}`,
        {}
    )
}

export async function getDashboardStats(
    params?: DashboardQueryParams
): Promise<ApiResponse<DashboardStats>> {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)

    const query = searchParams.toString()
    return apiRequest<DashboardStats>(
        `dashboard/stats${query ? `?${query}` : ''}`,
        {}
    )
}

export async function getDashboardCharts(
    params?: DashboardQueryParams
): Promise<ApiResponse<DashboardCharts>> {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.startDate) searchParams.set('startDate', params.startDate)
    if (params?.endDate) searchParams.set('endDate', params.endDate)

    const query = searchParams.toString()
    return apiRequest<DashboardCharts>(
        `dashboard/charts${query ? `?${query}` : ''}`,
        {}
    )
}

export async function getDashboardWidgets(): Promise<
    ApiResponse<DashboardWidgets>
> {
    return apiRequest<DashboardWidgets>('dashboard/widgets', {})
}

export async function getDashboardTables(): Promise<
    ApiResponse<DashboardTables>
> {
    return apiRequest<DashboardTables>('dashboard/tables', {})
}

// === LABELS AND COLORS ===

export const ORDER_STATUS_LABELS: Record<string, string> = {
    PENDING: 'Pendente',
    SCHEDULED: 'Agendado',
    CONFIRMED: 'Confirmado',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Concluído',
    CANCELED: 'Cancelado',
    CANCELLED: 'Cancelado',
    ON_THE_WAY: 'A Caminho',
    ARRIVED: 'Chegou',
}

export const ORDER_STATUS_COLORS: Record<string, string> = {
    PENDING: '#f59e0b',
    SCHEDULED: '#3b82f6',
    CONFIRMED: '#10b981',
    IN_PROGRESS: '#8b5cf6',
    COMPLETED: '#22c55e',
    CANCELED: '#ef4444',
    CANCELLED: '#ef4444',
    ON_THE_WAY: '#3b82f6',
    ARRIVED: '#10b981',
}

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
    PENDING: 'Pendente',
    PAID: 'Pago',
    REFUNDED: 'Reembolsado',
    FAILED: 'Falhou',
}

export const PAYMENT_STATUS_COLORS: Record<string, string> = {
    PENDING: '#f59e0b',
    PAID: '#22c55e',
    REFUNDED: '#3b82f6',
    FAILED: '#ef4444',
}

export const URGENCY_LABELS: Record<string, string> = {
    RED: 'Emergência',
    ORANGE: 'Muito Urgente',
    YELLOW: 'Urgente',
    GREEN: 'Pouco Urgente',
    BLUE: 'Não Urgente',
}

export const URGENCY_COLORS: Record<string, string> = {
    RED: '#ef4444',
    ORANGE: '#f97316',
    YELLOW: '#eab308',
    GREEN: '#22c55e',
    BLUE: '#3b82f6',
}

export const CONSULTATION_STATUS_LABELS: Record<string, string> = {
    SCHEDULED: 'Agendada',
    WAITING: 'Aguardando',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Concluída',
    CANCELLED: 'Cancelada',
    NO_SHOW: 'Não Compareceu',
}

export const CONSULTATION_STATUS_COLORS: Record<string, string> = {
    SCHEDULED: '#3b82f6',
    WAITING: '#f59e0b',
    IN_PROGRESS: '#8b5cf6',
    COMPLETED: '#22c55e',
    CANCELLED: '#ef4444',
    NO_SHOW: '#64748b',
}

export const PERIOD_LABELS: Record<DashboardPeriod, string> = {
    today: 'Hoje',
    week: 'Esta Semana',
    month: 'Este Mês',
    quarter: 'Este Trimestre',
    year: 'Este Ano',
    custom: 'Personalizado',
}

// === LEGACY SUPPORT (for account-page.tsx) ===

export interface DashboardData {
    stats: {
        atendimentos: number
        produtos: number
        setores: number
        atendentes: number
        funcionarios: number
    }
    teleconsultation?: {
        currentMinutes: number
        maxMinutes: number | 'unlimited'
        percentage: number
        isNearLimit: boolean
        isAtLimit: boolean
    }
    fiscalNotes?: {
        currentCount: number
        maxNotes: number | 'unlimited'
        percentage: number
        isNearLimit: boolean
        isAtLimit: boolean
    }
}

export async function getDashboardData(): Promise<ApiResponse<DashboardData>> {
    const response = await getFullDashboard({ period: 'month' })

    if (!response.success) {
        return { success: false, error: response.error, result: undefined as never }
    }

    const { stats, widgets } = response.result
    const tcMinutes = widgets.subscription.teleconsultationMinutes
    const fnNotes = widgets.subscription.fiscalNotes

    return {
        success: true,
        result: {
            stats: {
                atendimentos: stats.totalAttendances,
                produtos: stats.totalProducts,
                setores: stats.totalDepartments,
                atendentes: stats.totalAttendants,
                funcionarios: stats.totalEmployees,
            },
            teleconsultation: tcMinutes
                ? {
                    currentMinutes: tcMinutes.current,
                    maxMinutes: tcMinutes.max,
                    percentage: tcMinutes.percentage,
                    isNearLimit: tcMinutes.isNearLimit,
                    isAtLimit: tcMinutes.isAtLimit,
                }
                : undefined,
            fiscalNotes: fnNotes
                ? {
                    currentCount: fnNotes.current,
                    maxNotes: fnNotes.max,
                    percentage: fnNotes.percentage,
                    isNearLimit: fnNotes.isNearLimit,
                    isAtLimit: fnNotes.isAtLimit,
                }
                : undefined,
        },
    }
}
